import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock3 } from 'lucide-react';
import { Button } from './ui/Button';
import { getBlogPostingSchema, getBreadcrumbSchema } from '../seo/schema';

type ArticleSection = { title: string; content: ReactNode };

type ArticlePageProps = {
  title: string;
  description: string;
  path: string;
  category: string;
  datePublished: string;
  readTime: string;
  intro: string;
  summary: string[];
  sections: ArticleSection[];
};

export function ArticlePage({ title, description, path, category, datePublished, readTime, intro, summary, sections }: ArticlePageProps) {
  return (
    <article className="shoreline-article bg-verto-black px-4 py-14 text-verto-white md:py-20">
      <Helmet>
        <title>{title} | Verno Web</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://vernoweb.com${path}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://vernoweb.com${path}`} />
        <meta property="og:image" content="https://vernoweb.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBlogPostingSchema({ headline: title, description, urlPath: path, datePublished, dateModified: '2026-08-01' })) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Resources', path: '/blog' }, { name: title, path }])) }} />
      </Helmet>

      <div className="mx-auto max-w-4xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-verto-white/55 hover:text-verto-white"><ArrowLeft className="h-4 w-4" /> Back to resources</Link>
        <header className="shoreline-article__header mt-10 border-t border-verto-fog/30 bg-verto-stone p-7 md:p-12">
          <h1 className="display-title text-verto-white">{title}</h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-verto-white/65">{intro}</p>
          <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 border-t border-white/15 pt-5 text-xs font-semibold text-verto-fog"><span>{category}</span><span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" />{readTime} read</span><time dateTime={datePublished}>{new Date(`${datePublished}T12:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</time></div>
        </header>

        <section className="paper-record mt-14 bg-verto-mint p-7 pl-11 text-verto-black md:p-9 md:pl-14">
          <h2 className="font-display text-3xl font-bold">The useful version</h2>
          <ul className="mt-5 space-y-3 text-sm leading-6">{summary.map((item) => <li key={item} className="flex gap-3"><span aria-hidden="true">—</span><span>{item}</span></li>)}</ul>
        </section>

        <div className="mt-14 divide-y divide-white/15 border-y border-white/15">
          {sections.map((section, index) => (
            <section key={section.title} className="grid gap-5 py-10 md:grid-cols-[5rem_1fr] md:gap-9">
              <span className="font-display text-4xl font-bold text-verto-fog">{String(index + 1).padStart(2, '0')}</span>
              <div><h2 className="font-display text-3xl font-bold leading-none md:text-4xl">{section.title}</h2><div className="article-copy mt-5 space-y-4 text-base leading-7 text-verto-white/65">{section.content}</div></div>
            </section>
          ))}
        </div>

        <section className="mt-14 bg-verto-fog p-8 text-verto-black md:p-11">
          <h2 className="font-display text-4xl font-bold leading-none">See where the lead path breaks.</h2>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-verto-black/65">The lead-flow review maps the website, response, booking, and follow-up path before recommending a build.</p>
          <Button asChild className="mt-7"><Link to="/free-demo">Talk with Nate <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </section>
      </div>
    </article>
  );
}
